import {
  differenceInCalendarDays,
  format,
  isToday,
  isYesterday,
} from "date-fns";
import { enUS, ko, vi } from "date-fns/locale";
import moment from "moment";
import { useEffect, useState } from "react";
import { useInfoUser } from "../store/UserStore";

export const formatLastTime = (lastTime, languageMap, language) => {
  const currentTime = new Date();
  const timeDifference = currentTime - lastTime;

  const pluralize = (value, unit) => {
    if (language === "us" && value > 1) {
      return `${value} ${unit}s`;
    }
    return `${value} ${unit}`;
  };

  if (timeDifference < 60000) {
    return languageMap?.[`format.lastTime.few`] ?? "Few sec";
  } else if (timeDifference < 3600000) {
    const minutes = Math.floor(timeDifference / 60000);
    return pluralize(minutes, languageMap?.[`format.lastTime.min`] ?? "min");
  } else if (timeDifference < 86400000) {
    const hours = Math.floor(timeDifference / 3600000);
    return pluralize(hours, languageMap?.[`format.lastTime.hour`] ?? "hour");
  } else if (timeDifference < 691200000) {
    const days = Math.floor(timeDifference / 86400000);
    return pluralize(days, languageMap?.[`format.lastTime.day`] ?? "day");
  } else {
    return formatDate(lastTime);
  }
};

const LOCALES = {
  us: enUS,
  vn: vi,
  kr: ko,
};

export const formatShortTime = (time, languageMap, language) => {
  try {
    if (!time) return "";
    const date = new Date(time);
    const locale = LOCALES[language] || enUS;

    // if (isToday(date))
    //   return `${languageMap?.["locale.time.today"] || "Today"} ${format(date, "HH:mm")}`;
    // if (isYesterday(date))
    //   return `${languageMap?.["locale.time.yesterday"] || "Yesterday"} ${format(date, "HH:mm")}`;

    // const diff = differenceInCalendarDays(new Date(), date);
    // if (diff > 0 && diff < 7) {
    //   return `${format(date, "EEEE", { locale })} ${format(date, "HH:mm", { locale })}`;
    // }

    return format(date, "yy/MM/dd HH:mm", { locale });
  } catch {
    return "";
  }
};

export const formatDate = (time) => {
  if (!time) {
    return "-";
  }
  return format(new Date(time), "yy/MM/dd");
};

export const formatTime = (time) => {
  try {
    if (!time || !format(new Date(time), "yy/MM/dd HH:mm")) {
      return "";
    }
  } catch (error) {
    return "";
  }

  return format(new Date(time), "yyyy/MM/dd HH:mm");
};

export const formatSecondTime = (time) => {
  try {
    if (!time || !format(new Date(time), "yyyy/MM/dd HH:mm:ss")) {
      return "";
    }
  } catch (error) {
    return "";
  }

  return format(new Date(time), "yyyy/MM/dd HH:mm:ss");
};

export const stringToDate = (time) => {
  if (time === "OTHER") return time;

  const year = time?.substring(0, 4);
  const month = time?.substring(4, 6);
  const day = time?.substring(6, 8);
  return day + "-" + month + "-" + year;
};

export function formatTimeBySeconds(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return [
      hrs.toString().padStart(2, "0"),
      mins.toString().padStart(2, "0"),
      secs.toString().padStart(2, "0"),
    ].join(":");
  } else {
    return [
      mins.toString().padStart(2, "0"),
      secs.toString().padStart(2, "0"),
    ].join(":");
  }
}

// Thêm tham số locale và sửa lại mặc định thành "en-US" (en-EN không hợp lệ)
export function extractMonth(time, locale = "en-US") {
  if (!time) return null;
  return new Date(time).toLocaleString(locale, { month: "long" });
}

export function extractDay(time, locale = "en-US") {
  if (!time) return null;
  return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
    new Date(time),
  );
}

export function extractDate(time, locale = "en-US") {
  if (!time) return null;
  return new Intl.DateTimeFormat(locale, { day: "numeric" }).format(
    new Date(time),
  );
}

export function lastTimeOnlineUtils(personalDetail, languageMap, language) {
  if (!personalDetail) return "";

  const { online, lastTimeOnline } = personalDetail;

  if (online) {
    return languageMap?.[`format.lastTimeOnline.isOnline`] ?? "Đang hoạt động";
  }

  if (!lastTimeOnline) return "";

  const last = moment.utc(lastTimeOnline).local();

  const diffMs = moment().local().diff(last);
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) {
    return languageMap?.[`format.lastTimeOnline.fewSec`] ?? "Vừa truy cập";
  } else if (diffMin < 60) {
    return `${diffMin} ${languageMap?.[`format.lastTimeOnline.minute`] ?? "phút trước"}`;
  } else if (diffHour < 24) {
    return `${diffHour} ${languageMap?.[`format.lastTimeOnline.hour`] ?? "giờ trước"}`;
  } else if (diffDay === 1) {
    if (language === "kr") {
      let hour = parseInt(last.format("H"), 10);
      const minute = parseInt(last.format("m"), 10);
      const period = hour < 12 ? "오전" : "오후";
      if (hour === 0) hour = 12;
      if (hour > 12) hour -= 12;
      return `어제 ${period} ${hour}시 ${minute}분에`;
    }
    return `${languageMap?.[`format.lastTimeOnline.yesterday`] ?? "Hôm qua lúc"} ${last.format("HH:mm")}`;
  } else {
    return `${formatDate(lastTimeOnline)}`;
  }
}

export function useLastOnline(personalDetail) {
  const { user, languageMap } = useInfoUser();

  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 60 * 1000); // 1 phút

    return () => clearInterval(interval);
  }, []);

  return lastTimeOnlineUtils(personalDetail, languageMap, user?.language);
}
